import { CommonKeyObj, HighLightTextParams, TypeSearch } from "@/types";
import moment from "moment-timezone";

export default class CommonService {
  static async sleep(min: number, max: number) {
    return new Promise((resolve) => setTimeout(resolve, Math.floor(Math.random() * (max - min + 1)) + min));
  }

  static formatDate(date: string) {
    const formattedDate = moment(new Date(date)).format("YYYY/MM/DD HH:mm");
    return formattedDate === "Invalid date" ? date : formattedDate;
  }

  static base64urlEncode(input: string): string {
    const encoder = new TextEncoder();
    const bytes = encoder.encode(input);
    const base64 = btoa(String.fromCharCode(...bytes));
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  static base64urlDecode(base64url: string): string {
    let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) {
      base64 += "=";
    }

    const decodedData = atob(base64);
    const decoder = new TextDecoder("utf-8");
    const bytes = new Uint8Array(decodedData.length);
    for (let i = 0; i < decodedData.length; i++) {
      bytes[i] = decodedData.charCodeAt(i);
    }
    return decoder.decode(bytes);
  }

  static safeDecodeURIComponent(value: string) {
    /*
        MIT License
        Copyright (c) 2017 Justin Ridgewell
        https://github.com/jridgewell/safe-decode-uri-component
      */
    if (typeof value !== "string") return String(value);
    const HEX = Object.assign(Object.create(null), {
      "0": 0,
      "1": 1,
      "2": 2,
      "3": 3,
      "4": 4,
      "5": 5,
      "6": 6,
      "7": 7,
      "8": 8,
      "9": 9,
      a: 10,
      A: 10,
      b: 11,
      B: 11,
      c: 12,
      C: 12,
      d: 13,
      D: 13,
      e: 14,
      E: 14,
      f: 15,
      F: 15,
    });
    function hexCodeToInt(c: string, shift: number) {
      const i = HEX[c];
      return i === undefined ? 255 : i << shift;
    }

    /**
     * The below algorithm is based on Bjoern Hoehrmann's DFA Unicode Decoder.
     * Copyright (c) 2008-2009 Bjoern Hoehrmann <bjoern@hoehrmann.de>
     * See http://bjoern.hoehrmann.de/utf-8/decoder/dfa/ for details.
     */
    const UTF8_ACCEPT = 12;
    const UTF8_REJECT = 0;
    const UTF8_DATA = [
      // The first part of the table maps bytes to character to a transition.
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2,
      2, 2, 2, 2, 2, 2, 2, 2, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3,
      3, 3, 4, 4, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 6, 7, 7, 7,
      7, 7, 7, 7, 7, 7, 7, 7, 7, 8, 7, 7, 10, 9, 9, 9, 11, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4,

      // The second part of the table maps a state to a new state when adding a
      // transition.
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 12, 0, 0, 0, 0, 24, 36, 48, 60, 72, 84, 96, 0, 12, 12, 12, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 24, 0, 0, 0, 0, 0, 0, 0, 0, 0, 24, 24, 24, 0, 0, 0, 0, 0, 0, 0, 0, 0, 24, 24, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 48, 48, 48, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 48, 48, 0, 0, 0, 0, 0, 0, 0, 0, 0, 48, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0,

      // The third part maps the current transition to a mask that needs to apply
      // to the byte.
      0x7f, 0x3f, 0x3f, 0x3f, 0x00, 0x1f, 0x0f, 0x0f, 0x0f, 0x07, 0x07, 0x07,
    ];

    let state = UTF8_ACCEPT;
    function decode(codepoint: number, byte: number) {
      const type = UTF8_DATA[byte];
      state = UTF8_DATA[256 + state + type];
      return (codepoint << 6) | (byte & UTF8_DATA[364 + type]);
    }

    function newDecodeURIComponent(valueStr: string) {
      let k = valueStr.indexOf("%");
      if (k === -1) return valueStr;

      const length = valueStr.length;
      let decoded = "";
      let last = 0;
      let codepoint = 0;
      let startOfOctets = k;
      state = UTF8_ACCEPT;

      while (k > -1 && k < length - 2) {
        const high = hexCodeToInt(valueStr[k + 1], 4);
        const low = hexCodeToInt(valueStr[k + 2], 0);
        codepoint = decode(codepoint, high | low);

        switch (state) {
          case UTF8_ACCEPT:
            decoded += valueStr.substring(last, startOfOctets);

            decoded +=
              codepoint <= 0xffff
                ? String.fromCharCode(codepoint)
                : String.fromCharCode(0xd7c0 + (codepoint >> 10), 0xdc00 + (codepoint & 0x3ff));

            codepoint = 0;
            last = k + 3;
            k = startOfOctets = valueStr.indexOf("%", last);
            break;
          // Intentional fall-through
          case UTF8_REJECT:
            state = UTF8_ACCEPT;
            codepoint = 0;
            k = startOfOctets = valueStr.indexOf("%", startOfOctets + 1);
            break;
          default:
            k += 3;
            if (k < length && valueStr[k] === "%") break;
        }
      }

      return decoded + valueStr.substring(last);
    }

    return newDecodeURIComponent(value);
  }

  static parsePartitionKey(pk: string) {
    return {
      contractId: pk.split("#")[0],
      serviceGroupId: pk.split("#")[1],
    };
  }

  static generatePrefixS3Key(commonObj: CommonKeyObj, prefix: string[]) {
    const { contractId, serviceGroupId, baseUrlEncoded, date } = commonObj;
    const dateFolder = `${date.replace(/:/g, "_")}`;
    return `${contractId}/${serviceGroupId}/sites/${baseUrlEncoded}/${dateFolder}/${prefix.join("/")}`;
  }

  static generateSiteSummaryKey(commonObj: CommonKeyObj) {
    return {
      PK: `${commonObj.contractId}#${commonObj.serviceGroupId}#SiteSummary`,
      SK: `${commonObj.date}#${commonObj.baseUrlEncoded}`,
    };
  }

  static generateBaseUrlCountKey(commonObj: CommonKeyObj, crawlId: string, crawlHistoryId: string) {
    return {
      PK: `${commonObj.contractId}#${commonObj.serviceGroupId}#BaseUrlCount`,
      SK: `${commonObj.baseUrlEncoded}#${crawlId}#${crawlHistoryId}`,
    };
  }

  static debounce(func: any, wait: number) {
    let timeout: any;
    return () => {
      const args = [func, wait];
      const later = () => {
        timeout = null;
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  static highlightText({
    text,
    keywords = [],
    typeSearch = TypeSearch.SOURCE,
    caseSensitive = false,
  }: HighLightTextParams): string {
    keywords = keywords.map((keyword) => keyword.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"));
    if (keywords.includes("&lt;")) keywords.push("&amp;lt;");
    if (keywords.includes("&gt;")) keywords.push("&amp;gt;");

    if (typeSearch === TypeSearch.KEY_WORD) {
      return this.highlightKeyword({ text, keywords, caseSensitive });
    } else if (typeSearch === TypeSearch.SOURCE) {
      return this.highlightSourceCode({ text, keywords, caseSensitive });
    }
    return text;
  }

  static highlightKeyword({ text, keywords = [], caseSensitive = false }: HighLightTextParams): string {
    const htmlTagsRegex = /(<[^>]+>)/g;

    let htmlParts = text.split(htmlTagsRegex);

    // keep only text value
    const contentParts = [...htmlParts.map((part) => (part && !part.startsWith("<") ? part : ""))];
    const contentString = contentParts.join("");

    htmlParts = htmlParts.map((part) =>
      part?.startsWith("<") ? part.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;") : part,
    );

    for (const query of keywords) {
      if (!contentString.includes(query)) {
        // check query must be includes in whole string first
        continue;
      }

      let accumulateString = "";

      // get highlight by index
      const partIndices = contentParts
        .map((part, index) => {
          if (part === "") return null; // Skip empty parts
          if (query.includes(part) && contentString.includes(accumulateString)) {
            accumulateString += part;
            return { indexPartIndices: index, startIndex: 0, endIndex: part.length - 1, overlap: part };
          } else {
            const overlap1 = this.getOverlapString(part, query);
            const overlap2 = this.getOverlapString(query, part);
            if (
              contentString.includes(accumulateString + overlap1) ||
              contentString.includes(accumulateString + overlap2)
            ) {
              if (overlap1) {
                // it's overlap head of query
                const startIndex = part.indexOf(overlap1);
                const endIndex = part.length - 1;
                accumulateString += overlap1;

                return {
                  indexPartIndices: index,
                  startIndex,
                  endIndex,
                  overlap: overlap1,
                };
              } else if (overlap2) {
                // it's overlap tail of query
                const startIndex = 0;
                const endIndex = overlap2.length;
                accumulateString += overlap2;

                return {
                  indexPartIndices: index,
                  startIndex,
                  endIndex,
                  overlap: overlap2,
                };
              }
            } else if (part.includes(query) && query.length < part.length) {
              // it's overlap inside query
              const startIndex = part.indexOf(query);
              const endIndex = startIndex + query.length;
              accumulateString += query;

              return {
                indexPartIndices: index,
                startIndex,
                endIndex,
                overlap: query,
              };
            }

            return { indexPartIndices: index };
          }
        })
        .filter((item) => item?.overlap); // Remove null entries for empty parts

      // transfer highlight tag
      for (const p of partIndices) {
        if (p?.indexPartIndices && p.overlap) {
          const keywordRegex = new RegExp(`(${this.escapeRegExp(p.overlap)})`, caseSensitive ? "g" : "gi");
          htmlParts[p.indexPartIndices] = htmlParts[p.indexPartIndices].replace(
            keywordRegex,
            `<sb-highlight>$1</sb-highlight>`,
          );
        }
      }
    }

    return htmlParts.join("");
  }

  static highlightSourceCode({ text, keywords = [], caseSensitive = false }: HighLightTextParams): string {
    const escapedText = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    const sortedKeywords = [...keywords].sort((a, b) => b.length - a.length);

    let highlightedText = escapedText;
    sortedKeywords.forEach((keyword) => {
      const keywordRegex = new RegExp(`(${this.escapeRegExp(keyword)})`, caseSensitive ? "g" : "gi");
      highlightedText = highlightedText.replace(keywordRegex, `<sb-highlight>$1</sb-highlight>`);
    });

    return highlightedText;
  }

  static getOverlapString(str1: string, str2: string) {
    const maxLength = Math.min(str1.length, str2.length);

    for (let len = maxLength; len > 0; len--) {
      if (str1.slice(-len) === str2.slice(0, len)) {
        return str1.slice(-len);
      }
    }

    return false; // No overlap found
  }

  static escapeRegExp(str: string): string {
    return str.replace(/[.*+?^=!:${}()|\[\]\/\\]/g, "\\$&");
  }
}
